import { useQuery } from '@tanstack/react-query'
import { QueryKeys } from 'constants/query-keys'
import { BrandsTrendsProps } from 'types/api'
import { TIME } from 'utils/misc'
import { useAnalytics } from 'utils/analytics'
import { fetchBrandsTrends } from 'queries/brands/brand-trends'
import { useMemo, useState } from 'react'
import { ITitleListResponse } from 'utils/api'
import { filterRows, TitledTableData } from 'utils/data'
import { pickColumns } from '../../utils/data'

export function useGetBrandsTrends({
    env,
    accessToken,
    filters,
    location,
}: BrandsTrendsProps): ITitleListResponse {
    const { tenMinutes } = TIME
    const { trackEvent } = useAnalytics()
    const { data, isLoading, isError, refetch, remove, error } = useQuery(
        [QueryKeys.BRANDS_TRENDS, location, filters],
        () =>
            fetchBrandsTrends({
                env,
                accessToken,
                filters,
                method: 'GET',
                trackEvent,
            }),
        {
            staleTime: tenMinutes,
            cacheTime: tenMinutes,
            retry: false,
            enabled: !!filters?.brand_id,
        },
    )
    return { data, isLoading, isError, refetch, remove, error }
}

export const TimeFrames = ['TD', 'YST', 'WTD', 'MTD', 'YTD']
export type TimeFrame = (typeof TimeFrames)[number]

export function useFacetedBrandsTrends({
    frame,
    ...props
}: BrandsTrendsProps & { frame: TimeFrame }): ITitleListResponse & {
    setTimeframe: (frame: TimeFrame) => void
} {
    const { data, ...queryProps } = useGetBrandsTrends({ ...props })
    const [timeframe, setTimeframe] = useState<TimeFrame>(frame)
    const tableForTimeframe = useMemo<TitledTableData[] | undefined>(() => {
        if (!data) {
            return undefined
        }
        return data?.map((titledTable: TitledTableData) => {
            return {
                ...titledTable,
                table: pickColumns(
                    filterRows(
                        titledTable.table,
                        (row) => row[0].value.toUpperCase() === timeframe,
                    ),
                    [1, 2, 3],
                ),
            }
        })
    }, [timeframe, data])
    return { data: tableForTimeframe, setTimeframe, ...queryProps }
}
